import { withDeadline } from './phone-verification-flow.js';

export function formatPhoneRetryDuration(seconds, formatDuration) {
    const formatted = formatDuration(seconds);
    if (typeof formatted === 'string' && formatted.indexOf('common.time_') === -1) {
        return formatted;
    }
    const value = Math.max(1, Number(seconds) || 0);
    if (value >= 86400) return Math.ceil(value / 86400) + 'd';
    if (value >= 3600) return Math.ceil(value / 3600) + 'h';
    return Math.ceil(value / 60) + 'm';
}

export function createPhoneVerificationRecovery({
    abortController = () => new AbortController(),
    clearFallbackDeadline,
    clearPendingDeadline,
    maxAttempts = 4,
    now,
    onBlocked,
    onDeadlineExpired,
    onAttemptsExhausted = () => {},
    onResult,
    onTimeout,
    readFallbackDeadline,
    readPendingDeadline,
    requestStatus,
    retryMs,
    timers,
}) {
    let activeController = null;
    let failures = 0;
    let deadlineTimer = null;
    let generation = 0;
    let retryTimer = null;

    function invalidateRequest() {
        generation += 1;
        if (activeController) activeController.abort();
        activeController = null;
    }

    function clearRetry() {
        if (retryTimer !== null) timers.clearTimeout(retryTimer);
        retryTimer = null;
    }

    function expireDeadline() {
        deadlineTimer = null;
        clearRetry();
        invalidateRequest();
        clearPendingDeadline();
        clearFallbackDeadline();
        onDeadlineExpired();
    }

    function restoreLock(blockWithoutDeadline) {
        if (deadlineTimer !== null) timers.clearTimeout(deadlineTimer);
        deadlineTimer = null;
        const expiresAt = Math.max(
            Number(readPendingDeadline()) || 0,
            Number(readFallbackDeadline()) || 0,
        );
        const remainingMs = Math.max(0, expiresAt - now());
        if (remainingMs <= 0) {
            clearPendingDeadline();
            clearFallbackDeadline();
            if (blockWithoutDeadline) onBlocked(false);
            else onDeadlineExpired();
            return 0;
        }

        onBlocked(true);
        deadlineTimer = timers.setTimeout(expireDeadline, remainingMs);
        return remainingMs;
    }

    function scheduleRetry(requireDeadline) {
        const remainingMs = restoreLock(!requireDeadline);
        if (requireDeadline && remainingMs <= 0) return;
        if (!remainingMs && failures >= maxAttempts) {
            if (!remainingMs) onAttemptsExhausted();
            return;
        }
        if (retryTimer === null) {
            const delay = Math.min(
                retryMs * Math.pow(2, Math.max(0, failures - 1)),
                60000,
            );
            retryTimer = timers.setTimeout(run, delay);
        }
    }

    function run() {
        retryTimer = null;
        const hasDeadline = Math.max(
            Number(readPendingDeadline()) || 0,
            Number(readFallbackDeadline()) || 0,
        ) > now();
        if (!hasDeadline && failures >= maxAttempts) return;
        const controller = abortController();
        activeController = controller;
        const requestGeneration = ++generation;
        const request = Promise.resolve().then(() => requestStatus(controller.signal));
        withDeadline(request, retryMs, timers).then((result) => {
            if (requestGeneration !== generation) return;
            activeController = null;
            if (result.timedOut) {
                controller.abort();
                generation += 1;
                failures += 1;
                onTimeout();
                scheduleRetry(false);
                return;
            }
            if (result.error || !result.value.response.ok) {
                failures += 1;
                scheduleRetry(false);
                return;
            }
            failures = 0;
            if (onResult(result.value) === false) {
                scheduleRetry(true);
            } else {
                clearRetry();
            }
        });
    }

    return {
        clear() {
            clearRetry();
            invalidateRequest();
            if (deadlineTimer !== null) timers.clearTimeout(deadlineTimer);
            deadlineTimer = null;
        },
        restoreLock,
        start() {
            clearRetry();
            invalidateRequest();
            failures = 0;
            if (Math.max(Number(readPendingDeadline()) || 0, Number(readFallbackDeadline()) || 0)) {
                restoreLock(false);
            }
            run();
        },
    };
}
